/**
 * SplitterNode - Custom React Flow node for Splitter (EDITABLE)
 */
import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { SplitterType } from '../../types';
import { getSplitterLoss, getSplitterOutputs, getSplitterOutputLoss } from '../../utils/splitterCatalog';
import { useSplitterCatalogStore } from '../../stores/splitterCatalogStore';
import './FiberTopologyNodes.css';

export interface SplitterNodeData {
  label: string;
  type: SplitterType;
  poleId: number;
  splitterIndex: number;
  loss: number;
  cascadeRatio: number;
  editable?: boolean;
  inputPower?: number;
  outputs?: Array<{
    cable_id?: number;
    fiber?: number;
    /** Meno zákazníka na porte — zapisuje ho montážny dialóg pri pripojení. */
    customer_name?: string | null;
  }>;
  connectedOutputsCount?: number; // Number of outputs that have edges in topology
  isCustomerSplitter?: boolean; // Flag for customer cassette splitter
  backgroundColor?: string; // Custom background color
}

const SPLITTER_LOSS: Record<SplitterType, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 13.5,
  '1:32': 17.0,
  '1:64': 20.5,
};

export const SplitterNode: React.FC<NodeProps> = ({ id, data: nodeData, selected }) => {
  const data = nodeData as unknown as SplitterNodeData;
  const { updateNodeData } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);

  const handleTypeChange = (newType: SplitterType) => {
    const newLoss = getSplitterLoss(newType);
    updateNodeData(id as string, {
      ...data,
      type: newType,
      loss: newLoss,
    });
    setIsEditing(false);
  };

  const getSplitterRatio = (type: SplitterType): number => {
    return getSplitterOutputs(type);
  };

  const splitterCatalog = useSplitterCatalogStore((s) => s.types).filter((t) => t.is_active !== false);

  const ratio = getSplitterRatio(data.type);
  const isOverLimit = data.cascadeRatio > 64;

  // Calculate free ports based on visible connections in topology (not database outputs)
  // If connectedOutputsCount is provided, use it (counts actual edges in topology)
  // Otherwise fall back to counting outputs with cable_id
  const connectedPorts = data.connectedOutputsCount !== undefined
    ? data.connectedOutputsCount
    : data.outputs?.filter(out => out && out.cable_id !== undefined && out.cable_id !== null).length || 0;
  const freePorts = ratio - connectedPorts;

  return (
    <div
      className={`fiber-node splitter-node ${selected ? 'selected' : ''} ${isOverLimit ? 'over-limit' : ''}`}
      style={{
        backgroundColor: data.backgroundColor || undefined,
      }}
    >
      <div className="node-header" style={{ position: 'relative' }}>
        <span className="node-icon">{data.isCustomerSplitter ? '👤' : '🔀'}</span>
        <span className="node-title">{data.isCustomerSplitter ? 'Zákaznícky Splitter' : 'Splitter'}</span>
        {freePorts > 0 && (
          <span
            className="free-ports-badge"
            style={{
              background: '#4caf50',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: 'bold',
              marginLeft: '4px',
            }}
            title={`${freePorts} voľných portov z ${ratio}`}
          >
            {freePorts}
          </span>
        )}
        {freePorts === 0 && (
          <span
            className="free-ports-badge"
            style={{
              background: '#f44336',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: 'bold',
              marginLeft: '4px',
            }}
            title="Všetky porty obsadené"
          >
            0
          </span>
        )}
      </div>
      <div className="node-body">
        <div className="node-field">
          <span className="field-label">Typ:</span>
          {data.editable !== false ? (
            isEditing ? (
              <select
                className="field-select"
                value={data.type}
                onChange={(e) => handleTypeChange(e.target.value as SplitterType)}
                onBlur={() => setIsEditing(false)}
                autoFocus
              >
                {splitterCatalog.map((t) => (
                  <option key={t.id ?? t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
            ) : (
              <span
                className="field-value editable"
                onClick={() => setIsEditing(true)}
                title="Klikni na úpravu"
              >
                {data.type} ✏️
              </span>
            )
          ) : (
            <span className="field-value">{data.type}</span>
          )}
        </div>
        <div className="node-field">
          <span className="field-label">Loss:</span>
          <span className="field-value">{data.loss} dB</span>
        </div>
        <div className="node-field">
          <span className="field-label">Outputs:</span>
          <span className="field-value">{ratio}</span>
        </div>
        {data.cascadeRatio > 1 && (
          <div className={`node-field cascade-ratio ${isOverLimit ? 'error' : 'success'}`}>
            <span className="field-label">Cascade:</span>
            <span className="field-value">
              {isOverLimit ? '⚠️' : '✓'} 1:{data.cascadeRatio}
            </span>
          </div>
        )}
        <div className="node-field">
          <span className="field-label">Stĺpik:</span>
          <span className="field-value">{data.label}</span>
        </div>
      </div>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="fiber-handle target-handle"
        style={{ top: '50%' }}
      />

      {/* Dynamic output handles based on splitter ratio */}
      {Array.from({ length: ratio }).map((_, index) => {
        const totalOutputs = ratio;
        const spacing = 100 / (totalOutputs + 1);
        const topPosition = spacing * (index + 1);

        // Výkon TOHTO výstupu = vstup − strata tohto výstupu (asymetria: rôzna per port)
        const outputPower = data.inputPower !== undefined
          ? data.inputPower - getSplitterOutputLoss(data.type, index)
          : undefined;

        // Check if this output is connected
        const output = data.outputs?.[index];
        const isConnected = output && output.cable_id;

        return (
          <React.Fragment key={`output-${index}`}>
            <Handle
              type="source"
              position={Position.Right}
              id={`output-${index}`}
              className="fiber-handle source-handle"
              style={{
                top: `${topPosition}%`,
                background: isConnected ? '#4caf50' : '#ccc',
              }}
              title={[
                `Port ${index + 1}`,
                // Zákazník je na výstupe uložený, len ho sem doteraz nikto
                // nepreniesol — v topológii tak nebolo vidieť, kto na porte visí.
                output?.customer_name
                  ? `👤 ${String(output.customer_name).trim()}`
                  : (isConnected ? 'bez zákazníka' : 'voľný'),
                outputPower !== undefined ? `${outputPower.toFixed(2)} dBm` : '',
              ].filter(Boolean).join(' · ')}
            />
            {/* Show output power label - ALWAYS if power is available */}
            {outputPower !== undefined && (
              <div
                style={{
                  position: 'absolute',
                  right: '-60px',
                  top: `calc(${topPosition}% - 10px)`,
                  fontSize: '9px',
                  background: outputPower >= -8 ? '#4caf50' : outputPower >= -15 ? '#ffeb3b' : outputPower >= -25 ? '#ff9800' : '#f44336',
                  color: outputPower >= -15 ? '#000' : '#fff',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  pointerEvents: 'none',
                  opacity: isConnected ? 1 : 0.6, // Slightly transparent if not connected
                }}
              >
                {outputPower.toFixed(1)} dBm
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

